<template>
  <!-- Full-screen overlay when drop is active -->
  <Transition
    enter-active-class="transition-all duration-500 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-all duration-500 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="activeDrop && !claimed && !missed" class="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 backdrop-blur-lg">
      <!-- Pulsing background -->
      <div class="absolute inset-0 overflow-hidden">
        <div class="absolute inset-0 bg-gradient-to-br from-red-900/30 via-black to-orange-900/30 animate-pulse"></div>
        <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-red-500/10 animate-ping"></div>
      </div>

      <div class="relative z-10 w-full max-w-sm mx-4 text-center">
        <!-- Timer -->
        <div class="mb-6">
          <div class="inline-flex items-center gap-2 bg-red-600/30 border border-red-500 rounded-full px-4 py-2">
            <div class="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            <span class="text-red-400 font-mono font-bold text-lg">{{ formattedTime }}</span>
          </div>
        </div>

        <!-- Flash deal content -->
        <div class="bg-gray-900/80 backdrop-blur border border-red-500/50 rounded-3xl p-6 shadow-2xl shadow-red-500/20">
          <div class="text-xs text-red-400 uppercase tracking-widest font-bold mb-2">⚡ FLASH DROP ⚡</div>

          <h2 class="text-3xl font-black text-white mb-1">{{ activeDrop.title }}</h2>
          <p v-if="activeDrop.description" class="text-gray-400 text-sm mb-4">{{ activeDrop.description }}</p>

          <!-- Product -->
          <div class="bg-white/5 rounded-2xl p-4 mb-4">
            <div v-if="activeDrop.product?.imageUrl || activeDrop.product?.image_url" class="w-24 h-24 rounded-xl overflow-hidden mx-auto mb-3">
              <img :src="activeDrop.product.imageUrl || activeDrop.product.image_url" class="w-full h-full object-cover" />
            </div>
            <h3 class="text-white font-bold text-lg">{{ activeDrop.product?.name }}</h3>
            <div class="flex items-center justify-center gap-3 mt-2">
              <span class="text-gray-500 line-through text-lg">€{{ formatPrice(activeDrop.originalPrice) }}</span>
              <span class="text-3xl font-black text-green-400">€{{ formatPrice(activeDrop.dropPrice) }}</span>
            </div>
          </div>

          <!-- Stock indicator -->
          <div class="mb-4">
            <div class="flex justify-between text-xs text-gray-400 mb-1">
              <span>Verfügbar</span>
              <span class="text-red-400 font-bold">{{ remaining }} übrig!</span>
            </div>
            <div class="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                class="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full transition-all duration-500"
                :style="{ width: `${(remaining / activeDrop.totalQuantity) * 100}%` }"
              ></div>
            </div>
          </div>

          <!-- No-session warning -->
          <p v-if="noSession" class="text-amber-400 text-xs text-center mb-2">
            Scanne zuerst den QR-Code am Tisch, um zu bestellen und den Deal zu sichern!
          </p>

          <!-- Claim button -->
          <button
            @click="claim"
            :disabled="claiming"
            class="w-full py-4 rounded-2xl bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 text-white font-black text-xl uppercase tracking-wide hover:scale-[1.03] transition-transform disabled:opacity-50 relative overflow-hidden"
          >
            <span class="relative z-10">{{ claiming ? '...' : '🔥 JETZT CLAIMEN!' }}</span>
            <div class="absolute inset-0 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 animate-gradient"></div>
          </button>
        </div>

        <button @click="dismiss" class="mt-4 text-gray-500 text-sm hover:text-gray-300">Kein Interesse</button>
      </div>
    </div>
  </Transition>

  <!-- Success overlay -->
  <Transition
    enter-active-class="transition-all duration-500 ease-out"
    enter-from-class="opacity-0 scale-75"
    enter-to-class="opacity-100 scale-100"
  >
    <div v-if="claimed" class="fixed inset-0 z-[70] flex items-center justify-center bg-black/90">
      <div class="text-center max-w-sm mx-4">
        <div class="text-6xl mb-4 animate-bounce">🏆</div>
        <h2 class="text-3xl font-black text-white mb-2">DEAL GESICHERT!</h2>
        <p class="text-green-400 text-lg">{{ activeDrop?.product?.name }} für nur €{{ formatPrice(activeDrop?.dropPrice) }}</p>
        <button @click="dismissResult" class="mt-6 w-full py-3 rounded-xl bg-green-500 text-white font-bold">Weiter bestellen</button>
      </div>
    </div>
  </Transition>

  <!-- Miss / consolation overlay -->
  <Transition
    enter-active-class="transition-all duration-500 ease-out"
    enter-from-class="opacity-0 scale-75"
    enter-to-class="opacity-100 scale-100"
  >
    <div v-if="missed" class="fixed inset-0 z-[70] flex items-center justify-center bg-black/90">
      <div class="text-center max-w-sm mx-4">
        <div class="text-6xl mb-4">😢</div>
        <h2 class="text-2xl font-bold text-white mb-2">{{ missedMessage }}</h2>

        <div v-if="consolation" class="mt-4 bg-white/10 rounded-2xl p-4">
          <p class="text-amber-400 font-medium">Dein Trost-Deal:</p>
          <h3 class="text-white font-bold text-lg mt-1">{{ consolation.product?.name }}</h3>
          <div class="flex items-center justify-center gap-2 mt-2">
            <span class="text-gray-400 line-through">€{{ formatPrice(consolation.product?.price) }}</span>
            <span class="text-green-400 font-bold text-xl">€{{ formatPrice(consolation.discountedPrice) }}</span>
            <span class="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full">-{{ consolation.discountPercentage }}%</span>
          </div>
          <button
            @click="addConsolationToCart"
            class="w-full mt-3 py-2 rounded-xl bg-amber-500 text-white font-bold"
          >In den Warenkorb</button>
        </div>

        <button @click="dismissResult" class="mt-4 text-gray-500 text-sm hover:text-gray-300">Schließen</button>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useGamificationStore } from '@/stores/gamification'
import { useCartStore } from '@/stores/cart'

const props = defineProps({
  orderSessionId: [Number, String],
  sessionIdentifier: String,
})

const store = useGamificationStore()
const cartStore = useCartStore()
let pollInterval = null

const CLAIMED_DROPS_KEY = 'claimedDrops'
const getClaimedDropsList = () => { try { return JSON.parse(localStorage.getItem(CLAIMED_DROPS_KEY) || '[]') } catch { return [] } }
const isDropAlreadyClaimed = (id) => getClaimedDropsList().includes(id)
const saveDropAsClaimed = (id) => { const arr = getClaimedDropsList(); if (!arr.includes(id)) { arr.push(id); localStorage.setItem(CLAIMED_DROPS_KEY, JSON.stringify(arr)) } }

onMounted(() => {
  if (store.features.battle_royale || store.features.battleRoyale) {
    store.loadActiveDrop()
    pollInterval = setInterval(() => {
      store.loadActiveDrop()
    }, 30000)
  }
})

const claiming = ref(false)
const claimed = ref(false)
const missed = ref(false)
const missedMessage = ref('')
const consolation = ref(null)
const timeRemaining = ref(0)
let timerInterval = null

const activeDrop = computed(() => {
  const drop = store.activeDrop
  if (!drop || !drop.id) return null
  if (isDropAlreadyClaimed(drop.id)) return null
  return drop
})
const remaining = computed(() => {
  if (!activeDrop.value) return 0
  return Math.max(0, activeDrop.value.totalQuantity - activeDrop.value.claimedQuantity)
})

const formattedTime = computed(() => {
  const min = Math.floor(timeRemaining.value / 60)
  const sec = timeRemaining.value % 60
  return `${min}:${sec.toString().padStart(2, '0')}`
})

watch(activeDrop, (drop) => {
  if (drop) {
    startTimer()
    triggerHaptic()
  }
}, { immediate: true })

function startTimer() {
  if (timerInterval) clearInterval(timerInterval)
  updateTimeRemaining()
  timerInterval = setInterval(() => {
    updateTimeRemaining()
    if (timeRemaining.value <= 0) {
      clearInterval(timerInterval)
    }
  }, 1000)
}

function updateTimeRemaining() {
  if (!activeDrop.value?.endsAt) {
    timeRemaining.value = 0
    return
  }
  const end = new Date(activeDrop.value.endsAt).getTime()
  timeRemaining.value = Math.max(0, Math.floor((end - Date.now()) / 1000))
}

function formatPrice(price) {
  return parseFloat(price || 0).toFixed(2)
}

function triggerHaptic() {
  if ('vibrate' in navigator) {
    navigator.vibrate([100, 50, 100])
  }
}

const noSession = ref(false)

async function claim() {
  if (!activeDrop.value || claiming.value) return

  const sessionId = props.orderSessionId || parseInt(localStorage.getItem('sessionNumericId')) || null
  const identifier = props.sessionIdentifier || localStorage.getItem('sessionUniqueId') || localStorage.getItem('sessionId') || ''

  if (!sessionId || !identifier) {
    noSession.value = true
    return
  }

  claiming.value = true
  try {
    const dropId = activeDrop.value.id
    await store.claimDrop(dropId, sessionId, identifier)
    claimed.value = true
    saveDropAsClaimed(dropId)
    triggerHaptic()
    addClaimedToCart()
  } catch (e) {
    const result = store.claimResult
    if (result?.failed) {
      missed.value = true
      missedMessage.value = result.message || 'Zu spät!'
      consolation.value = result.consolation
    } else if (result?.alreadyClaimed) {
      missed.value = true
      missedMessage.value = result.message || 'Du hast bereits geclaimt.'
      if (activeDrop.value?.id) saveDropAsClaimed(activeDrop.value.id)
    } else {
      missed.value = true
      missedMessage.value = 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.'
    }
  } finally {
    claiming.value = false
  }
}

async function addClaimedToCart() {
  const drop = activeDrop.value
  if (!drop?.product) return
  const product = drop.product
  await cartStore.addItem(
    {
      id: product.id || product.productId || `flash-${Date.now()}`,
      name: product.name || 'Flash Drop',
      price: parseFloat(drop.dropPrice || 0),
      imageUrl: product.imageUrl || product.image_url || '',
    },
    1, [], ''
  )
}

async function addConsolationToCart() {
  if (!consolation.value?.product) return
  await cartStore.addItem(
    { id: consolation.value.product.id, name: consolation.value.product.name, price: consolation.value.discountedPrice },
    1, [], ''
  )
  dismissResult()
}

function dismiss() {
  store.activeDrop = null
}

function dismissResult() {
  claimed.value = false
  missed.value = false
  consolation.value = null
  store.dismissClaimResult()
  store.activeDrop = null
}

onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval)
  if (pollInterval) clearInterval(pollInterval)
})
</script>

<style scoped>
@keyframes gradient {
  0%, 100% { opacity: 0; }
  50% { opacity: 0.3; }
}
.animate-gradient {
  animation: gradient 1.5s ease-in-out infinite;
}
</style>
